import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';

import { Store } from '@ngxs/store';
import { AssetManagementState } from '../../../../shared/state/AssetManagementState';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { Limit } from '../../models/Limit';
import { AssetManagementService } from '../../services/asset-management.service';
import { generateMRTLine } from '../../AssetManagementTestHelper';
import { MRT } from '../../models/MRT';
import {
  ActionType,
  DispositionType,
  SpecLine,
  SpecificationType,
  enforceNumberField,
  getActionType,
  getDispositionType
} from '../../models/SpecLine';
import { ReparLimitSizes } from '../../models/request/UpdateLimit';
import { Subject, takeUntil } from 'rxjs';
import { TranslationState } from '../../../../shared/state/TranslationState';
@Component({
  selector: 'app-repair-limit',
  templateUrl: './repair-limit.component.html',
  styleUrls: ['./repair-limit.component.scss'],
  standalone: false
})
export class RepairLimitComponent implements OnInit, OnChanges {
  @Input() limit: Limit;
  @Input() specLine: SpecLine;
  @Input() index: number;
  @Input() editMode: boolean;
  protected translationService = inject(TranslationService);
  public specificationFormGroup = inject(FormGroupDirective);
  public store = inject(Store);
  private assetManagementService = inject(AssetManagementService);

  private readonly ngUnsubscribe = new Subject<void>();
  isDefault: boolean;
  formGroup: FormGroup;
  header: string;
  viewMode: string;
  maxCrownSizes: string[];
  maxShoulderSizes: string[];
  maxSidewallSizes: string[];
  rNailShoulderSizes: string[];
  mrtToValidate: MRT;
  groupName = '';

  constructor() {}

  ngOnInit(): void {
    this.rNailShoulderSizes = [ReparLimitSizes.RSR22, ReparLimitSizes.RSR24, ReparLimitSizes.RSR26];
    this.maxCrownSizes = [ReparLimitSizes.SR33, ReparLimitSizes.SR35, ReparLimitSizes.SR37];
    this.maxShoulderSizes = [ReparLimitSizes.SR40, ReparLimitSizes.SR42, ReparLimitSizes.SR44];
    this.maxSidewallSizes = [ReparLimitSizes.SR40, ReparLimitSizes.SR42, ReparLimitSizes.SR44];

    this.viewMode = this.editMode ? 'edit-component' : 'view-component';
    this.groupName = 'limit' + this.index;
    this.formGroup = this.specificationFormGroup.control.get(this.groupName) as FormGroup;
    this.prepopulateForm();

    // revalidating the form

    this.formGroup.get('totalRepairs').valueChanges.subscribe(() => {
      this.formGroup.get('totalNail')?.updateValueAndValidity();
      this.formGroup.get('totalRnailCrown')?.updateValueAndValidity();
      this.formGroup.get('totalRnailShoulder')?.updateValueAndValidity();
      this.formGroup.get('totalSection')?.updateValueAndValidity();
    });

    this.formGroup.get('totalSection').valueChanges.subscribe(() => {
      this.formGroup.get('totalCrownSection')?.updateValueAndValidity();
      this.formGroup.get('totalShoulderSection')?.updateValueAndValidity();
      this.formGroup.get('totalSidewallSection')?.updateValueAndValidity();
    });
    //Look for the current Application and get correct MRT
    this.mrtToValidate = generateMRTLine();
    this.assetManagementService.getCurrentApplication().subscribe((x) => {
      if (this.store.selectSnapshot(AssetManagementState.mrtSpecifications)) {
        this.mrtToValidate = this.store.selectSnapshot(AssetManagementState.mrtSpecifications).get(x);
      }
      if (!this.mrtToValidate) {
        this.mrtToValidate = generateMRTLine();
      }
    });
    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.prepopulateForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('editMode' in changes) {
      this.viewMode = changes.editMode.currentValue ? 'edit-component' : 'view-component';
      this.prepopulateForm();
    }
    if (this.specificationFormGroup && 'limit' in changes) {
      this.groupName = 'limit' + this.index;
      this.formGroup = this.specificationFormGroup.control.get(this.groupName) as FormGroup;
      this.isDefault = this.specificationFormGroup.control.get('isDefault').value;
      this.prepopulateForm();
    }
  }

  prepopulateForm(): void {
    this.formGroup.patchValue({
      totalRepairs: this.limit.totalRepairLimits.totalRepairs,
      totalNail: this.limit.totalRepairLimits.nailRepairs,
      totalRnailCrown: this.limit.totalRepairLimits.rnailCrownRepairs,
      totalRnailShoulder: this.limit.totalRepairLimits.rnailShoulder.repairs,
      rNailShoulderSize: this.limit.totalRepairLimits.rnailShoulder.shoulder.size,
      totalSection: this.limit.totalRepairLimits.section.repairs,
      totalCrownSection: this.limit.totalRepairLimits.section.crown.count,
      maxCrownSize: this.limit.totalRepairLimits.section.crown.size,
      totalShoulderSection: this.limit.totalRepairLimits.section.shoulder.count,
      maxShoulderSize: this.limit.totalRepairLimits.section.shoulder.size,
      totalSidewallSection: this.limit.totalRepairLimits.section.sidewall.count,
      maxSidewallSize: this.limit.totalRepairLimits.section.sidewall.size,
      spot: this.limit.otherRepairLimits.spot,
      bead: this.limit.otherRepairLimits.bead,
      liner: this.limit.otherRepairLimits.liner,
      brakeSkid: this.limit.otherRepairLimits.brake,
      action: this.translationService.translate(getActionType(this.specLine.repair.action)),
      disposition: this.translationService.translate(getDispositionType(this.specLine.repair.disposition)),
      disposalCode: this.specLine.repair.disposalCode,
      application: this.specLine.repair.application,
      axle: this.specLine.repair.axle,
      treads: this.specLine.repair.treads
    });
  }

  checkValue(event: any, formControlName: string) {
    if (event.target.value.length > 2) {
      this.formGroup.get(formControlName).patchValue(event.target.value.slice(0, 2));
    }
  }

  protected readonly SpecType = SpecificationType;
  protected readonly ActionType = ActionType;
  protected readonly DispositionType = DispositionType;
  protected readonly enforceNumberField = enforceNumberField;
}
